/**
 * useDispose / useDisposeMany — declarative Three.js resource cleanup.
 *
 * Collapses the recurring `useEffect(() => () => resource?.dispose(), [resource])`
 * idiom used throughout viewport renderers. Pass a single resource to
 * `useDispose`, or a list to `useDisposeMany` for multi-resource cleanup blocks.
 *
 * Both hooks accept `null` / `undefined` values without complaint — the
 * dispose call is only issued for truthy entries.
 */

import { useEffect } from "react";

interface Disposable {
  dispose: () => void;
}

/**
 * Dispose a single Three.js resource (material, geometry, texture, render
 * target, etc.) when it changes or the component unmounts. No-op when the
 * resource is nullish.
 */
export function useDispose(resource: Disposable | null | undefined): void {
  useEffect(() => {
    return () => {
      resource?.dispose();
    };
  }, [resource]);
}

/**
 * Dispose multiple Three.js resources as one effect. Each resource may be
 * null/undefined. The cleanup runs when any member of the list changes or
 * the component unmounts — same semantics as a hand-written `return () =>
 * { a?.dispose(); b?.dispose(); }` block, but without the boilerplate.
 */
export function useDisposeMany(
  ...resources: Array<Disposable | null | undefined>
): void {
  useEffect(() => {
    return () => {
      for (const r of resources) r?.dispose();
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: variadic deps
  }, resources);
}
